import { useHistory } from 'react-router-dom'
import MenuItem from './MenuItem'
import { useSelector } from 'react-redux'
import { IStore } from '@src/store'
import GreenMenuItem from './GreenMenuItem'
import { ModalType, openModalTask } from '../../modalComponent/modalComponent'
import styles from '../index.module.scss'
import { BonusExpireStamp } from '@src/components/BonusExpireStamp/BonusExpireStamp'
import { SpecialBonusStamp } from '@src/components/SpecialBonusStamp/SpecialBonusStamp'
import classNames from 'classnames'
import { RakeBackStamp } from '@src/components/RakeBackStamp'
import { useTranslation } from 'react-i18next'
import GiftIcon from '@src/components/icons/gift'

import SwordsIcon from '@src/components/icons/swords'
import ESportsIcon from '@src/components/icons/eSports'
import SportsIcon from '@src/components/icons/sports'
import ChipIcon from '@src/components/icons/chip'
import CrownIcon from '@src/components/icons/crown'
import BlogIcon from '@src/components/icons/blog'
import HeadphonesIcon from '@src/components/icons/headphones'
import JackToken from '@src/assets/menu/jackToken.svg'
import BecomeInvestorIcon from '@src/components/icons/becomeInvestor'
import HomeIcon from '@src/components/icons/home'
import SubMenuItem from './SubMenuItem'
import DealerIcon from '@src/components/icons/dealer'
import CherryIcon from '@src/components/icons/cherry'
import Heart21Icon from '@src/components/icons/heart21'
import FootballIcon from '@src/components/icons/football'
import TennisIcon from '@src/components/icons/tennis'
import DartsIcon from '@src/components/icons/darts'
import BasketballIcon from '@src/components/icons/basketball'
import { convertToK, formatNumberWithCommas, isUserLoggedIn } from '@src/utils/functions'
import ProvidersIcon from '@src/components/icons/providers'
import ProgressMenuItem from './ProgressMenuItem/ProgressMenuItem'
import StarIcon from '@src/components/icons/star'
import { useBounties } from '@src/context/BountiesContext'
import useEliteSeatProgressUpdated from '@src/hooks/useEliteSeatProgressUpdated'
import PriceIndicator from '@src/components/PriceIndicator/PriceIndicator'

import type { User } from '@src/stores/auth/user'
import useBJFMining from '@src/hooks/useBJFMining'
import { LanguageSelector } from './LanguageSelector/LanguageSelector'
import { useIntercom } from '@src/hooks/useIntercom'
import MartiniIcon from '@src/components/icons/martini'
import FireIcon from '@src/components/icons/fire'

interface Props {
  me?: User
}

const DesktopMenu = ({ me }: Props) => {
  const { t } = useTranslation()
  const history = useHistory()
  const collapsedMenu = useSelector((state: IStore) => state.app.collapsedMenu)
  const isLoggedIn = isUserLoggedIn()
  const { bountyProgress, isBountyActive, bountyWagerLeft, expiresStamp, bonus, setBountyModalVisible } = useBounties()
  const { nextBonus, wagerLeft, currentTier, progressbarProgress } = useEliteSeatProgressUpdated()
  const { miningProgressPercentage, miningWagerLeft, miningAmount } = useBJFMining()
  const { supportClick } = useIntercom()
  const isBonusPage = Boolean(me && me.featureFlags?.includes('bonuses'));
  
  return (
    <>
      <div className={`${styles.scrollableContent} h-full flex flex-col gap-8`}>
        <PriceIndicator isCollapsed={collapsedMenu} />
        {isLoggedIn && (
          <>
            <ProgressMenuItem
              title={t('menu.progressItem.mineBjf', 'Mine BJF')}
              isCollapsed={collapsedMenu}
              icon={<JackToken />}
              progress={String(miningProgressPercentage)}
              wagerLeft={miningWagerLeft < 1000 ? formatNumberWithCommas(miningWagerLeft) : convertToK(miningWagerLeft)}
              expiresAt={null}
              showClaim={false}
              customTimerText={t(
                'menu.progressItem.wagerMoreToMine',
                'Wager ${{amount}} more to mine {{miningAmount}}',
                {
                  amount: miningWagerLeft,
                  miningAmount,
                }
              )}
              onClickAction={() => history.push('/profile?tab=account&stab=BJF+Mining')}
              progressBarColor="#3CCEA2"
            />
            <ProgressMenuItem
              title={t('menu.progressItem.level', 'LEVEL {{level}}', { level: currentTier })}
              isCollapsed={collapsedMenu}
              icon={<ProvidersIcon />}
              progress={progressbarProgress}
              wagerLeft={wagerLeft < 1000 ? formatNumberWithCommas(wagerLeft) : convertToK(wagerLeft)}
              expiresAt={null}
              showClaim={false}
              customTimerText={t('menu.progressItem.nextLevelUpBonus', 'Next Level Up Bonus: ${{amount}}', {
                amount: nextBonus,
              })}
              onClickAction={() => history.push('/profile?tab=account&stab=Elite')}
              progressBarColor="#3CCEA2"
            />
          </>
        )}
        {isBountyActive && (
          <ProgressMenuItem
            title={t('menu.progressItem.bounty', 'BOUNTY')}
            isCollapsed={collapsedMenu}
            icon={<StarIcon />}
            progress={bountyProgress}
            wagerLeft={bountyWagerLeft}
            expiresAt={expiresStamp}
            showClaim={bonus?.meta?.finished && bonus?.meta?.bounty}
            customTimerText={null}
            onClickAction={() => setBountyModalVisible(true)}
            progressBarColor={null}
          />
        )}
        <MenuItem
          isCollapsed={collapsedMenu}
          label={t('menu.home', 'Home')}
          onAction={() => history.push('/')}
          icon={<HomeIcon />}
          linkPath="/"
        ></MenuItem>
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<SwordsIcon />}
          label={t('menu.competitions', 'Competitions')}
          onAction={() => history.push('/competitions')}
          linkPath="/competitions"
        />
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<ChipIcon />}
          label={t('menu.casino', 'Casino')}
          linkPath="/casino"
          onAction={() => history.push('/casino')}
        >
          <SubMenuItem Icon={CherryIcon} label={t('casino.categories.slots', 'Slots')} linkPath="/casino?tab=slots" />
          <SubMenuItem
            Icon={DealerIcon}
            label={t('casino.categories.live', 'Live Casino')}
            linkPath="/casino?tab=live"
          />
          <SubMenuItem
            Icon={MartiniIcon}
            label={t('casino.categories.gameshows', 'Game Shows')}
            linkPath="/casino?tab=gameshows"
          />
          <SubMenuItem Icon={FireIcon} label={t('casino.categories.bjf', 'BJF Originals')} linkPath="/casino?tab=bjf" />
          <SubMenuItem
            Icon={Heart21Icon}
            label={t('casino.categories.blackjack', 'Blackjack')}
            linkPath="/casino?tab=blackjack"
          />
          <SubMenuItem
            Icon={SportsIcon}
            label={t('casino.categories.sports', 'Sports')}
            linkPath="/casino?tab=sports"
          />
          <SubMenuItem Icon={ProvidersIcon} label={t('menu.providers', 'Providers')} linkPath="/providers" />
        </MenuItem>
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<DealerIcon className={'text-white'} />}
          label={t('menu.liveGames', 'Live Games')}
          onAction={() => history.push('/casino?tab=live')}
          linkPath="/live-games"
          collapsedOnly
        />
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<CherryIcon className={'text-white'} />}
          label={t('menu.slots', 'Slots')}
          onAction={() => history.push('/casino?tab=slots')}
          linkPath="/slots"
          collapsedOnly
        />
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<ProvidersIcon className={'text-white'} />}
          label={t('menu.providers', 'Providers')}
          onAction={() => history.push('/providers')}
          linkPath="/providers"
          collapsedOnly
        />
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<SportsIcon />}
          label={t('menu.sports', 'Sports')}
          onAction={() => history.push('/game/btsg_sportbetting')}
          linkPath="/game/btsg_sportbetting"
        >
          <SubMenuItem
            Icon={FootballIcon}
            label={t('menu.football', 'Football')}
            linkPath="/game/btsg_sportbetting/football"
          />
          <SubMenuItem Icon={TennisIcon} label={t('menu.tennis', 'Tennis')} linkPath="/game/btsg_sportbetting/tennis" />
          <SubMenuItem Icon={DartsIcon} label={t('menu.darts', 'Darts')} linkPath="/game/btsg_sportbetting/darts" />
          <SubMenuItem
            Icon={BasketballIcon}
            label={t('menu.basketball', 'Basketball')}
            linkPath="/game/btsg_sportbetting/basketball"
          />
        </MenuItem>
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<ESportsIcon />}
          label={t('menu.eSports', 'eSports')}
          onAction={() => history.push('/game/odn_oddingame')}
          linkPath="/game/odn_oddingame"
        />
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<CrownIcon />}
          label={t('menu.affiliate', 'Affiliate')}
          onAction={() => history.push('/profile?tab=affiliate')}
          visible={isLoggedIn}
        />
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<BlogIcon />}
          label={t('menu.blog', 'Blog')}
          onAction={() => history.push('/#blog')}
          linkPath='/#blog'
        />
        {isBonusPage && <MenuItem
          isCollapsed={collapsedMenu}
          icon={<GiftIcon />}
          label={t('menu.bonuses', 'Bonuses')}
          onAction={() => history.push('/bonuses')}
          linkPath="/bonuses"
        />}
        <LanguageSelector collapsedMenu={collapsedMenu} />
        <div className={collapsedMenu ? 'flex flex-col gap-8' : ''}>
          <GreenMenuItem
            isCollapsed={collapsedMenu}
            icon={<BecomeInvestorIcon />}
            label={t('menu.becomeAnInvestor', 'Become an investor')}
            onAction={() => openModalTask.next({ type: ModalType.beacomeAnInvestor })}
            visible={isLoggedIn}
          />
          <GreenMenuItem
            isCollapsed={collapsedMenu}
            icon={<JackToken />}
            label={t('menu.bjfToken', 'BJF Token')}
            onAction={() => ''}
            to="/bjf-token"
            isNew
          />
          {/* <GreenMenuItem
            isCollapsed={collapsedMenu}
            icon={<BuyJackIcon />}
            label={t('menu.buyJack', 'Buy Jack')}
            onAction={() => openModalTask.next({ type: ModalType.buyJack })}
            isNew
            visible={isLoggedIn}
          /> */}
        </div>
      </div>
      <div className={`flex flex-col gap-10 mx-5`}>
        <SpecialBonusStamp me={me} isCollapsed={collapsedMenu} />
        <BonusExpireStamp />
        <RakeBackStamp
          rakebackClassNames={classNames(styles.rakeback, 'text-sm leading-5 font-inter font-bold')}
          rakebackPercentageClassNames={styles.rakeback_percentage}
        />
      </div>
      <div>
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<HeadphonesIcon />}
          label={t('menu.liveSupport', 'Live support')}
          onAction={supportClick}
        />
      </div>
    </>
  )
}

export default DesktopMenu
